import { cs } from '../../lib/currency';
/**
 * Customer-facing Desk Display.
 *
 * Read-only, public (URL contains an unguessable session UUID). A tablet
 * at the front desk points to /desk-display/booking/:sessionId and the
 * customer watches their booking get built in real-time as the admin
 * types in the Walk-in Booking modal.
 *
 * Polls /api/desk-display/sessions/:sessionId every 1.5s. Renders big,
 * legible text optimised for a 10"–13" tablet at arm's length.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Sparkles } from 'lucide-react';

const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

const POLL_INTERVAL_MS = 1500;

export default function DeskDisplay() {
  const { sessionId } = useParams();
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState('connecting'); // connecting | active | ended | error
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    const apiBase = `${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/desk-display/sessions/${sessionId}`;

    const tick = async () => {
      if (stopRef.current) return;
      try {
        const { data } = await axios.get(apiBase, { timeout: 4000 });
        setSnapshot(data.snapshot || {});
        setStatus(data.status === 'confirmed' ? 'confirmed' : 'active');
      } catch (e) {
        if (e?.response?.status === 404) {
          setStatus('ended');
          stopRef.current = true;
          return;
        }
        // transient → leave the last snapshot up, try again
      }
      if (!stopRef.current) setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  // -----------------------------------------------------------------
  // States
  // -----------------------------------------------------------------
  if (status === 'ended') {
    return (
      <Shell>
        <div className="text-center py-20">
          <div className="text-2xl font-semibold text-slate-700 mb-2">Session ended</div>
          <div className="text-sm text-slate-500">Ask the staff for a new display link.</div>
        </div>
      </Shell>
    );
  }

  if (status === 'connecting' || !snapshot) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="h-6 w-6 mr-2 animate-spin" /> Connecting to front desk…
        </div>
      </Shell>
    );
  }

  const price = snapshot.price || {};
  const confirmed = status === 'confirmed';

  return (
    <Shell>
      {confirmed && (
        <div className="bg-emerald-600 text-white py-3 px-6 rounded-lg mb-6 flex items-center gap-2 text-lg" data-testid="desk-display-confirmed">
          <CheckCircle2 className="h-5 w-5" />
          Booking confirmed — see you soon!
        </div>
      )}
      <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1" data-testid="desk-display-greeting">
        {snapshot.customer_name
          ? <>Hi, <span className="text-blue-700">{snapshot.customer_name}</span></>
          : 'Welcome'}
      </div>
      <div className="text-sm text-slate-500 mb-8">Your booking — live preview</div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card title="Lane & time">
          <Row label="Lane" value={snapshot.lane_name || '—'} />
          <Row label="Date" value={snapshot.date || '—'} />
          <Row
            label="Time"
            value={
              snapshot.start_time
                ? `${snapshot.start_time}${snapshot.duration_minutes ? ` · ${snapshot.duration_minutes} min` : ''}`
                : '—'
            }
          />
          {snapshot.notes && <Row label="Notes" value={snapshot.notes} />}
        </Card>

        <Card title="Equipment">
          {Array.isArray(snapshot.equipment_lines) && snapshot.equipment_lines.length > 0 ? (
            <ul className="space-y-1.5">
              {snapshot.equipment_lines.map((l, i) => (
                <li key={i} className="flex justify-between text-base" data-testid={`desk-display-equip-${i}`}>
                  <span>{l.name} ×{l.qty}</span>
                  <span className="font-mono">{fmt(l.line_total)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-slate-400 text-sm">None</div>
          )}
        </Card>
      </div>

      <Card title="Price breakdown" testId="desk-display-price-card">
        <Row label="Lane" value={fmt(price.lane_price)} mono />
        {price.equipment_total > 0 && <Row label="Equipment" value={fmt(price.equipment_total)} mono />}
        <Row label="Subtotal" value={fmt(price.subtotal)} mono />
        {price.discount > 0 && <Row label="Discount" value={`- ${fmt(price.discount)}`} mono className="text-emerald-700" />}
        {price.cc_surcharge_amount > 0 && (
          <Row
            label={`Card processing fee (${price.cc_surcharge_percent}%)`}
            value={`+ ${fmt(price.cc_surcharge_amount)}`}
            mono
            className="text-amber-700"
            testId="desk-display-cc-fee"
          />
        )}
        <div className="border-t border-slate-300 mt-3 pt-3 flex justify-between items-baseline">
          <span className="text-lg font-medium text-slate-800">Total</span>
          <span className="text-3xl font-bold text-slate-900 font-mono" data-testid="desk-display-total">
            {fmt(price.total || 0)}
          </span>
        </div>
        {snapshot.payment_method_label && (
          <div className="mt-3 text-sm text-slate-600 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-purple-600" />
            Paying with: <span className="font-medium text-slate-800">{snapshot.payment_method_label}</span>
          </div>
        )}
      </Card>

      <div className="text-center text-[11px] text-slate-300 mt-8">
        Live preview · refreshes every 1.5s
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-8 sm:px-12 sm:py-12">
    <div className="max-w-3xl mx-auto">{children}</div>
  </div>
);

const Card = ({ title, children, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={testId}>
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</div>
    {children}
  </div>
);

const Row = ({ label, value, mono, className, testId }) => (
  <div className={`flex justify-between text-base py-1 ${className || 'text-slate-700'}`} data-testid={testId}>
    <span>{label}</span>
    <span className={mono ? 'font-mono' : ''}>{value}</span>
  </div>
);
